import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, ArrowRight, Loader2, Flame, Newspaper, ExternalLink } from "lucide-react";
import { labelForTopic, categoryForTopic } from "../../data/communityCategories";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const asArray = (d) => (Array.isArray(d) ? d : d?.posts || []);
const engagement = (p) => (p.comments || 0) * 2 + (p.likes || 0);

const postTitle = (p) => {
  if (p.title && p.title.trim()) return p.title.trim();
  const line = (p.content || "").replace(/\s+/g, " ").trim();
  return line ? (line.length > 44 ? line.slice(0, 44) + "…" : line) : "(제목 없음)";
};
const excerpt = (p, n = 78) => {
  const line = (p.content || "").replace(/\s+/g, " ").trim();
  return line.length > n ? line.slice(0, n) + "…" : line;
};

function Meta({ p, showCat = true }) {
  return (
    <span className="inline-flex items-center gap-2 text-[12px] text-stone-400 whitespace-nowrap">
      {showCat && p.topic && <span className="text-burgundy-500/80 font-medium">{labelForTopic(p.topic)}</span>}
      {showCat && p.topic && <span className="text-stone-300">·</span>}
      <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3.5 h-3.5" />{p.comments || 0}</span>
      <span className="inline-flex items-center gap-0.5"><Heart className="w-3.5 h-3.5" />{p.likes || 0}</span>
    </span>
  );
}

function TitleRow({ p, onClick }) {
  return (
    <button
      onClick={() => onClick(p.id)}
      className="w-full text-left flex items-start justify-between gap-3 py-2.5 group"
      data-testid={`mag-row-${p.id}`}
    >
      <span className="text-[14.5px] text-stone-800 leading-snug group-hover:text-burgundy-700 transition-colors line-clamp-1 flex-1">
        {postTitle(p)}
      </span>
      <span className="shrink-0 mt-0.5"><Meta p={p} showCat={false} /></span>
    </button>
  );
}

function SectionShell({ emoji, title, wide, moreLabel, moreTo, children, testid }) {
  const navigate = useNavigate();
  return (
    <section
      className={`bg-white rounded-2xl border border-stone-100 p-4 sm:p-5 ${wide ? "ring-1 ring-burgundy-100/60" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-1">
        <h2 className={`font-serif ${wide ? "text-[19px]" : "text-[17px]"} text-stone-900 flex items-center gap-2`}>
          <span aria-hidden>{emoji}</span> {title}
        </h2>
        {moreTo && (
          <button
            onClick={() => navigate(moreTo)}
            className="text-[12.5px] font-semibold text-burgundy-600 hover:text-burgundy-700 inline-flex items-center gap-0.5 whitespace-nowrap"
            data-testid={`${testid}-more`}
          >
            {moreLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function EmptyLine() {
  return <p className="text-[13px] text-stone-400 py-4 text-center">아직 이야기가 없어요. 첫 이야기를 들려주세요 🌿</p>;
}

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

// 카테고리 카드 하단 컴팩트 NEWS (외부 기사 — 회원 글 아래 보완용)
function NewsBlock({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-stone-200 space-y-2" data-testid="mag-news-block">
      {items.map((n) => (
        <div key={n.id} className="flex items-start gap-2" data-testid={`mag-news-${n.id}`}>
          <span className="shrink-0 mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-burgundy-500 bg-burgundy-50 px-1.5 py-0.5 rounded">
            <Newspaper className="w-3 h-3" /> NEWS
          </span>
          <div className="min-w-0 flex-1">
            <a href={n.url} target="_blank" rel="noopener noreferrer" className="block text-[13.5px] font-medium text-stone-700 hover:text-burgundy-700 leading-snug line-clamp-2">
              {n.korean_title}
            </a>
            <div className="flex items-center gap-1.5 text-[11px] text-stone-400 mt-0.5">
              <span className="truncate max-w-[120px]">{n.source_name}</span>
              <span>·</span>
              <span>{fmtDate(n.published_date)}</span>
              <a href={n.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-burgundy-500 hover:text-burgundy-700 font-semibold ml-auto whitespace-nowrap">
                원문 보기 <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MagazineHome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ hot: [], love: [], loveFeatured: null, essays: [], travel: [], health: [], hobby: [], retirement: [], free: [] });
  const [news, setNews] = useState({});

  const openPost = (id) => navigate(`/community/post/${id}`);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/community-news?per_category=2`).then((r) => r.json()).then((d) => { if (alive) setNews(d || {}); }).catch(() => {});
    Promise.all([
      fetch(`${API_URL}/api/posts?sort=popular&limit=40`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/posts?limit=200`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/posts?essay=true&limit=6`).then((r) => r.json()).catch(() => []),
    ]).then(([popRaw, recentRaw, essayRaw]) => {
      if (!alive) return;
      const pop = asArray(popRaw);
      const recent = asArray(recentRaw);
      const essays = asArray(essayRaw)
        .slice()
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      const isReal = (p) => !p.is_essay && !p.is_event_notice && p.topic !== "notice";
      const hot = pop.filter(isReal).slice(0, 3);

      const buckets = { relationships: [], travel: [], health: [], hobby: [], retirement: [], free: [] };
      recent
        .filter((p) => !p.is_essay)
        .forEach((p) => {
          const cat = categoryForTopic(p.topic);
          if (buckets[cat]) buckets[cat].push(p);
        });

      const love = buckets.relationships.slice();
      const loveFeatured = love.slice().sort((a, b) => engagement(b) - engagement(a))[0] || null;
      const loveRest = love.filter((p) => !loveFeatured || p.id !== loveFeatured.id).slice(0, 5);

      setData({
        hot,
        love: loveRest,
        loveFeatured,
        essays,
        travel: buckets.travel.slice(0, 5),
        health: buckets.health.slice(0, 5),
        hobby: buckets.hobby.slice(0, 5),
        retirement: buckets.retirement.slice(0, 5),
        free: buckets.free.slice(0, 5),
      });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="magazine-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" />
      </div>
    );
  }

  const featuredEssay = data.essays[0];
  const recentEssays = data.essays.slice(1, 4);
  const div = "divide-y divide-stone-100";
  const newsFor = (cat, memberCount) => (news[cat] || []).slice(0, memberCount < 2 ? 2 : 1);
  const loveCount = (data.loveFeatured ? 1 : 0) + data.love.length;

  return (
    <div className="space-y-4" data-testid="magazine-home">
      {/* 🔥 오늘 많이 보는 이야기 */}
      <SectionShell emoji="🔥" title="오늘 많이 보는 이야기" testid="mag-hot">
        {data.hot.length === 0 ? <EmptyLine /> : (
          <div className={div}>
            {data.hot.map((p, i) => (
              <button key={p.id} onClick={() => openPost(p.id)} className="w-full text-left flex items-start gap-3 py-3 group" data-testid={`mag-hot-${p.id}`}>
                <span className="shrink-0 w-6 h-6 rounded-full bg-burgundy-50 text-burgundy-600 text-[12px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-stone-800 group-hover:text-burgundy-700 transition-colors leading-snug line-clamp-1">{postTitle(p)}</span>
                  <span className="mt-1 block"><Meta p={p} /></span>
                </span>
              </button>
            ))}
          </div>
        )}
      </SectionShell>

      {/* ❤️ 연애와 사랑 (가장 중요한 메인 카테고리) */}
      <SectionShell emoji="❤️" title="연애와 사랑" wide moreLabel="연애와 사랑 더보기" moreTo="/community?cat=relationships" testid="mag-love">
        {!data.loveFeatured && data.love.length === 0 ? <EmptyLine /> : (
          <>
            {data.loveFeatured && (
              <button onClick={() => openPost(data.loveFeatured.id)} className="w-full text-left block rounded-xl bg-gradient-to-br from-rose-50/70 to-stone-50 border border-rose-100 p-3.5 mb-1 group" data-testid="mag-love-featured">
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-burgundy-600 bg-white/70 px-2 py-0.5 rounded-full mb-1.5"><Flame className="w-3 h-3" /> 오늘 가장 많이 본 글</span>
                <span className="block text-[16px] font-bold text-stone-900 group-hover:text-burgundy-700 transition-colors leading-snug">{postTitle(data.loveFeatured)}</span>
                <span className="mt-1.5 block"><Meta p={data.loveFeatured} showCat={false} /></span>
              </button>
            )}
            <div className={div}>
              {data.love.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}
            </div>
          </>
        )}
        <NewsBlock items={newsFor("relationships", loveCount)} />
      </SectionShell>

      {/* ✍️ L양의 연애 에세이 — 시그니처 코너 */}
      <SectionShell emoji="✍️" title="L양의 연애 에세이" moreLabel="L양의 연애 에세이 더보기" moreTo="/community?cat=relationships&sort=essay" testid="mag-essay">
        {!featuredEssay ? <EmptyLine /> : (
          <>
            <button onClick={() => openPost(featuredEssay.id)} className="w-full text-left block group" data-testid="mag-essay-featured">
              <span className="block font-serif text-[17px] font-bold text-stone-900 group-hover:text-burgundy-700 transition-colors leading-snug">{postTitle(featuredEssay)}</span>
              <span className="block text-[13.5px] text-stone-500 leading-relaxed mt-1.5">{excerpt(featuredEssay)}</span>
              <span className="inline-flex items-center gap-0.5 text-[13px] font-semibold text-burgundy-600 mt-2">에세이 읽기 <ArrowRight className="w-3.5 h-3.5" /></span>
            </button>
            {recentEssays.length > 0 && (
              <div className={`${div} mt-2 border-t border-stone-100`}>
                {recentEssays.map((p) => (
                  <button key={p.id} onClick={() => openPost(p.id)} className="w-full text-left py-2.5 text-[14px] text-stone-700 hover:text-burgundy-700 transition-colors leading-snug line-clamp-1 flex items-center gap-2" data-testid={`mag-essay-row-${p.id}`}>
                    <span className="text-burgundy-300">✍️</span> {postTitle(p)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </SectionShell>

      {/* ✈️ 여행 | 🩺 건강 (2열) */}
      <div className="grid md:grid-cols-2 gap-4">
        <SectionShell emoji="✈️" title="여행" moreLabel="여행 더보기" moreTo="/community?cat=travel" testid="mag-travel">
          {data.travel.length === 0 ? <EmptyLine /> : <div className={div}>{data.travel.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}</div>}
          <NewsBlock items={newsFor("travel", data.travel.length)} />
        </SectionShell>
        <SectionShell emoji="🩺" title="건강" moreLabel="건강 더보기" moreTo="/community?cat=health" testid="mag-health">
          {data.health.length === 0 ? <EmptyLine /> : <div className={div}>{data.health.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}</div>}
          <NewsBlock items={newsFor("health", data.health.length)} />
        </SectionShell>
      </div>

      {/* 🎨 취미 | 🌿 노후 & 인생2막 (2열) */}
      <div className="grid md:grid-cols-2 gap-4">
        <SectionShell emoji="🎨" title="취미" moreLabel="취미 더보기" moreTo="/community?cat=hobby" testid="mag-hobby">
          {data.hobby.length === 0 ? <EmptyLine /> : <div className={div}>{data.hobby.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}</div>}
          <NewsBlock items={newsFor("hobby", data.hobby.length)} />
        </SectionShell>
        <SectionShell emoji="🌿" title="노후 & 인생2막" moreLabel="인생2막 더보기" moreTo="/community?cat=retirement" testid="mag-retirement">
          {data.retirement.length === 0 ? <EmptyLine /> : <div className={div}>{data.retirement.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}</div>}
          <NewsBlock items={newsFor("retirement", data.retirement.length)} />
        </SectionShell>
      </div>

      {/* 💬 자유게시판 */}
      <SectionShell emoji="💬" title="자유게시판" moreLabel="자유게시판 더보기" moreTo="/community?cat=free" testid="mag-free">
        {data.free.length === 0 ? <EmptyLine /> : <div className={div}>{data.free.map((p) => <TitleRow key={p.id} p={p} onClick={openPost} />)}</div>}
      </SectionShell>
    </div>
  );
}
